import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApi, type HealthResponse, ApiError } from '../api/client';
import { WhanauDirectory } from './WhanauDirectory';

/**
 * Dedicated whānau directory view at /directory.
 *
 * The home screen (HomeView at "/") owns the landing experience —
 * greeting, whakataukī, stat tiles, action cards. This page is the
 * directory's own surface: a back-to-home link, a heading, the
 * directory itself.
 *
 * Developer affordances (the API health check) remain available here
 * via ?debug=1 — useful for IT triage but not part of the kaimahi
 * surface.
 */
export function DirectoryView() {
  const { t } = useTranslation();
  const callApi = useApi();

  // Developer mode: ?debug=1 in the URL surfaces the API health-check
  // diagnostic. Kept out of the everyday kaimahi view; intentional opt-in
  // for IT triage.
  const debugMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('debug') === '1';

  const [healthState, setHealthState] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ok'; data: HealthResponse }
    | { kind: 'error'; message: string; status?: number }
  >({ kind: 'idle' });

  const handleHealthCheck = async () => {
    setHealthState({ kind: 'loading' });
    try {
      const result = await callApi<HealthResponse>('/health');
      setHealthState({ kind: 'ok', data: result });
    } catch (err) {
      if (err instanceof ApiError) {
        setHealthState({
          kind: 'error',
          status: err.status,
          message: err.message,
        });
      } else {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setHealthState({ kind: 'error', message });
      }
    }
  };

  return (
    <>
      <WhanauDirectory />

      {debugMode && (
        <div className="health-check" aria-label={t('health.diagnosticLabel')}>
          <p className="health-check__heading">{t('health.diagnostic')}</p>
          <button
            type="button"
            className="auth-button auth-button--secondary"
            onClick={handleHealthCheck}
            disabled={healthState.kind === 'loading'}
          >
            {healthState.kind === 'loading'
              ? t('health.checking')
              : t('health.test')}
          </button>
          {healthState.kind === 'ok' && (
            <pre className="health-check__result health-check__result--ok">
              {JSON.stringify(healthState.data, null, 2)}
            </pre>
          )}
          {healthState.kind === 'error' && (
            <pre className="health-check__result health-check__result--error">
              {healthState.status
                ? `${String(healthState.status)} — ${healthState.message}`
                : healthState.message}
            </pre>
          )}
        </div>
      )}
    </>
  );
}

export default DirectoryView;
